import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Input,
  Row,
  Col,
  Space,
  Card,
  Dropdown,
  Menu,
  Tag,
  Select,
  notification,
  Checkbox,
} from "antd";
import {
  EyeOutlined,
  SolutionOutlined,
  SearchOutlined,
  UnorderedListOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from "@ant-design/icons";
import DefaultLayout from "../../components/layout/DefaultLayout";
import Header from "../../components/header";
import globalSearchService from "../../services/globalSearch";
import "./index.css";

const { Option } = Select;

const GlobalSearch = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });
  const [visible, setVisible] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    field: "created_at",
    direction: "desc",
  });
  const [forms, setForms] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [formsLoading, setFormsLoading] = useState(false);

  const getGlobalSearchData = async (
    query = "",
    page = 1,
    pageSize = pagination.pageSize
  ) => {
    if (!selectedFormId) {
      notification.warning({
        message: "Please select a Form Type before searching.",
      });
      return;
    }
    if (query.trim().length < 3 && query.trim() !== "") {
      notification.warning({
        message: "Search requires at least 3 characters",
      });
      return;
    }

    setLoading(true);
    try {
      const params = {
        q: query,
        page,
        sort_by: sortConfig.field,
        order_by: sortConfig.direction,
        form_id: selectedFormId,
        per_page: pageSize || 15,
      };
      const response = await globalSearchService.searcGlobal(params);

      setData(response.data);
      setPagination({
        current: response.current_page,
        pageSize: response.per_page,
        total: response.total,
      });
    } catch (error) {
      console.error("Error in global search:", error);
      notification.error({
        message: "Error",
        description: "Failed to fetch search results",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    getGlobalSearchData(searchQuery, 1, pagination.pageSize); // Reset to first page on new search
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleSortChange = (
    field,
    direction,
    pageSize = pagination.pageSize
  ) => {
    const newDirection =
      direction ||
      (sortConfig.field === field && sortConfig.direction === "asc"
        ? "desc"
        : "asc");

    setSortConfig({ field, direction: newDirection });
    // Always use the current pageSize when sorting
    getGlobalSearchData(searchQuery, 1, pageSize);
  };

  const handleFormChange = (formId) => {
    setSelectedFormId(formId);
    // Do not trigger search or notification on form change
  };

  const handleTableChange = (tablePagination, filters, sorter) => {
    // If sorting changed, handle sort and reset to first page
    if (
      sorter &&
      sorter.field &&
      (sorter.field !== sortConfig.field ||
        (sorter.order &&
          sorter.order.replace("end", "") !== sortConfig.direction))
    ) {
      handleSortChange(
        sorter.field,
        sorter.order === "ascend" ? "asc" : "desc",
        tablePagination.pageSize
      );
      return;
    }
    // If page or pageSize changed, fetch new data
    if (
      tablePagination.current !== pagination.current ||
      tablePagination.pageSize !== pagination.pageSize
    ) {
      getGlobalSearchData(
        searchQuery,
        tablePagination.current,
        tablePagination.pageSize
      );
    }
  };

  const columns = [
    {
      title: "S.No",
      dataIndex: "serial",
      key: "serial",
      width: 40,
      render: (_, __, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "Form",
      dataIndex: "model",
      key: "model",
      width: 180,
      sorter: {
        compare: (a, b) => a.model.localeCompare(b.model),
        multiple: 1,
      },
      sortOrder: sortConfig.field === "model" ? sortConfig.direction : null,
    },
    {
      title: "Sequence No",
      dataIndex: "sequence_no",
      key: "sequence_no",
      width: 200,
      sorter: {
        compare: (a, b) => a.sequence_no.localeCompare(b.sequence_no),
        multiple: 2,
      },
      sortOrder:
        sortConfig.field === "sequence_no" ? sortConfig.direction : null,
    },
    {
      title: "Request Title",
      dataIndex: "request_title",
      key: "request_title",
      width: 300,
      sorter: {
        compare: (a, b) => a.request_title.localeCompare(b.request_title),
        multiple: 3,
      },
      sortOrder:
        sortConfig.field === "request_title" ? sortConfig.direction : null,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <Tag color={status === "Approved" ? "green" : "orange"}>{status}</Tag>
      ),
      sorter: {
        compare: (a, b) => a.status.localeCompare(b.status),
        multiple: 4,
      },
      sortOrder: sortConfig.field === "status" ? sortConfig.direction : null,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
      sorter: {
        compare: (a, b) => new Date(a.created_at) - new Date(b.created_at),
        multiple: 5,
      },
      sortOrder:
        sortConfig.field === "created_at" ? sortConfig.direction : null,
    },
    {
      title: "Actions",
      key: "Actions",
      width: 80,
      render: (text, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => (window.location.href = record.url)}
        />
      ),
      fixed: "right",
    },
  ];

  const [visibleColumns, setVisibleColumns] = useState(() =>
    columns.map((column) => column.key)
  );

  const menuItems = columns.map((column) => ({
    key: column.key,
    label: (
      <Checkbox
        className="mr-5"
        style={{ fontSize: "12px" }}
        onChange={() => handleColumnToggle(column.key)}
        checked={visibleColumns.includes(column.key)}
      >
        {column.title}
      </Checkbox>
    ),
  }));

  const handleColumnToggle = (key) => {
    const updatedVisibleColumns = [...visibleColumns];

    if (visibleColumns.includes(key)) {
      const index = updatedVisibleColumns.indexOf(key);
      if (index > -1) {
        updatedVisibleColumns.splice(index, 1);
      }
    } else {
      updatedVisibleColumns.push(key);
    }

    setVisibleColumns(updatedVisibleColumns);
    localStorage.setItem(
      "visibleColumnsgs",
      JSON.stringify(updatedVisibleColumns)
    );
  };

  const fetchForms = async () => {
    setFormsLoading(true);
    try {
      const response = await globalSearchService.getAllForms();
      // Try to handle both array and object with data property
      console.log("Forms response:", response);
      let formsData = [];
      if (Array.isArray(response)) {
        formsData = response;
      } else if (Array.isArray(response.data)) {
        formsData = response.data;
      }
      setForms(formsData);
    } catch (error) {
      console.error("Error fetching forms:", error);
      notification.error({
        message: "Error",
        description: "Failed to fetch form types",
      });
      setForms([]);
    } finally {
      setFormsLoading(false);
    }
  };

  useEffect(() => {
    fetchForms();

    const savedVisibleColumns = localStorage.getItem("visibleColumnsgs");
    if (savedVisibleColumns) {
      setVisibleColumns(JSON.parse(savedVisibleColumns));
    }
    // Do not call getGlobalSearchData() on mount; only fetch forms and columns
  }, []);

  const tableData =
    data?.map((item) => ({
      id: item.data?.id,
      request_title: item.data?.request_title,
      sequence_no: item.data?.sequence_no,
      status: item.data?.status,
      created_at: item.data?.created_at,
      url: item.url,
      model: item.model,
    })) ?? [];

  return (
    <DefaultLayout>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Header icon={<SolutionOutlined />} title="Global Search Forms" />
        </Col>
      </Row>
      <Card>
        <Space>
          <Input
            size="large"
            placeholder="Enter search term (min 3 characters)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            allowClear
            style={{ width: 400 }}
          />
          <Select
            showSearch
            style={{ width: 250 }}
            placeholder="Select Form Type"
            optionFilterProp="children"
            loading={formsLoading}
            onChange={handleFormChange}
            value={selectedFormId}
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {forms.map((form) => (
              <Option key={form.id} value={form.id}>
                {form.name}
              </Option>
            ))}
          </Select>
          <Select
            value={sortConfig.field}
            style={{ width: 180 }}
            onChange={(value) => {
              // If the same field is selected, toggle direction
              if (value === sortConfig.field) {
                handleSortChange(
                  value,
                  sortConfig.direction === "asc" ? "desc" : "asc"
                );
              } else {
                handleSortChange(value, "asc");
              }
            }}
            suffixIcon={
              sortConfig.direction === "asc" ? (
                <SortAscendingOutlined />
              ) : (
                <SortDescendingOutlined />
              )
            }
          >
            <Option value="created_at">Created At</Option>
            <Option value="request_title">Request Title</Option>
            <Option value="sequence_no">Sequence No</Option>
            <Option value="status">Status</Option>
          </Select>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearch}
            loading={loading}
            disabled={!searchQuery || searchQuery.trim().length < 3}
          >
            Search
          </Button>
        </Space>
      </Card>

      {data.length > 0 && (
        <Card>
          <Dropdown
            className="mr-10"
            menu={{ items: menuItems }}
            open={visible}
            onOpenChange={(isVisible) => setVisible(isVisible)}
            trigger={["click"]}
          >
            <span
              style={{ color: "black", fontSize: "11px", color: "gray" }}
              onClick={(e) => e.preventDefault()}
            >
              <UnorderedListOutlined /> Columns
            </span>
          </Dropdown>

          <Table
            style={{ minHeight: "100vh" }}
            onChange={handleTableChange}
            columns={columns.filter((column) =>
              visibleColumns.includes(column.key)
            )}
            dataSource={tableData}
            rowKey={(record) => record.id}
            scroll={{ x: 1200 }}
            sticky={true}
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `Total ${total} items`,
              pageSizeOptions: ["10", "15", "20", "50"],
            }}
          />
        </Card>
      )}
    </DefaultLayout>
  );
};

export default GlobalSearch;
